import {useState} from 'react';
import DataTable from './DataTable.jsx';
import EditDrawer from './EditDrawer.jsx';
import {kr} from '../lib/format.js';
import {api} from '../lib/auth.js';

const yesNo = v => (v === true ? 'ja' : v === false ? 'nej' : '—');
const money = k => r => (r[k] == null ? '—' : kr(r[k]));

/** A raw Fortnox field as a column: headed by its own name, since that is what the edit
 *  drawer and Fortnox's docs call it. */
const fieldColumn = key => ({key, label: key, render: r => {
	const v = r[key];
	if (typeof v === 'boolean') return yesNo(v);
	return v == null || v === '' ? '—' : String(v);
}});

/**
 * Column specs per collection. `render` gets the whole row, so a column can combine
 * fields — a voucher's series and number read as one identifier, not two.
 */
/** Order matters: it is the tab order. Reference data first, then the documents
 * that reference it, ending at the vouchers everything resolves into. */
export const VIEWS = {
	articles: {
		label: 'Artiklar',
		columns: [
			{key: '_id', label: 'Artikel'},
			{key: 'name', label: 'Benämning'},
			{key: 'stream', label: 'Ström'},
			{key: 'account', label: 'Konto'},
			{key: 'settings_key', label: 'Inställningsnyckel', render: r => r.settings_key ?? '—'},
			{key: 'is_mrr', label: 'MRR', render: r => (r.is_mrr ? 'ja' : '—')},
			{key: 'invoiced_2026_h1', label: 'Fakturerat jan–jul',
				render: r => (r.invoiced_2026_h1 == null ? 'ej delbart' : kr(r.invoiced_2026_h1))},
		],
	},
	customers: {
		label: 'Kunder',
		filters: [{key: 'active', label: 'Alla',
			options: [{value: 'true', label: 'Aktiva'}, {value: 'false', label: 'Inaktiva'}]}],
		// The rest of the columns are every editable field, served with the rows — see
		// `fieldColumn` in DataTable. Name rides in the pinned first cell.
		columns: [
			{key: 'Name', label: 'Kund', render: r => `${r._id}  ${r.Name ?? ''}`, strong: true},
		],
		fieldColumns: true,
		wide: true,
	},
	suppliers: {
		label: 'Leverantörer',
		columns: [
			{key: '_id', label: 'Nr'},
			{key: 'Name', label: 'Namn'},
			{key: 'OrganisationNumber', label: 'Orgnr'},
			{key: 'City', label: 'Ort'},
			{key: 'Currency', label: 'Valuta'},
		],
	},
	'customer-invoices': {
		label: 'Kundfakturor',
		filters: [{key: 'booked', label: 'Alla',
			options: [{value: 'true', label: 'Bokförda'}, {value: 'false', label: 'Ej bokförda'}]}],
		columns: [
			{key: '_id', label: 'Nummer'},
			{key: 'InvoiceDate', label: 'Datum'},
			{key: 'CustomerName', label: 'Kund'},
			{key: 'period', label: 'Period',
				render: r => (r.InvoicePeriodStart ? `${r.InvoicePeriodStart} – ${r.InvoicePeriodEnd}` : '—')},
			{key: 'Total', label: 'Belopp', render: money('Total'), strong: true},
			{key: 'Booked', label: 'Bokförd', render: r => yesNo(r.Booked)},
		],
	},
	'supplier-invoices': {
		label: 'Leverantörsfakturor',
		filters: [{key: 'booked', label: 'Alla',
			options: [{value: 'true', label: 'Bokförda'}, {value: 'false', label: 'Ej bokförda'}]}],
		columns: [
			{key: '_id', label: 'Nummer'},
			{key: 'InvoiceDate', label: 'Datum'},
			{key: 'SupplierName', label: 'Leverantör'},
			{key: 'DueDate', label: 'Förfaller'},
			{key: 'Total', label: 'Belopp', render: money('Total'), strong: true},
			{key: 'Balance', label: 'Kvar', render: money('Balance')},
		],
	},
	vouchers: {
		label: 'Verifikat',
		filters: [{key: 'year', label: 'Alla år',
			options: [2026, 2025, 2024, 2023, 2022, 2021].map(y => ({value: y, label: y}))}],
		columns: [
			{key: '_id', label: 'Verifikat', render: r => `${r.series}${r.number}`},
			{key: 'date', label: 'Datum'},
			{key: 'description', label: 'Beskrivning'},
			{key: 'reference_type', label: 'Typ', render: r => r.reference_type ?? '—'},
			{key: 'row_count', label: 'Rader'},
			{key: 'debit_total', label: 'Debet', render: money('debit_total'), strong: true},
		],
	},
};

/** The full document, fetched only when a row is opened — this is where rows live. */
function Detail({collection, id, onClose}) {
	const [doc, setDoc] = useState(null);
	useState(() => {
		api(`/api/browse/${collection}/${encodeURIComponent(id)}`)
			.then(r => r.json()).then(setDoc).catch(() => setDoc({error: true}));
	});
	const lines = doc?.rows ?? doc?.InvoiceRows ?? null;
	return (
		<div className="drawer" onClick={onClose}>
			<div className="drawer__panel" onClick={e => e.stopPropagation()}>
				<div className="drawer__head">
					<span className="drawer__title">{id}</span>
					<button className="btn drawer__close" onClick={onClose}>Stäng</button>
				</div>
				{!doc && <div className="state">Hämtar…</div>}
				{doc?.error && <div className="state">Kunde inte hämta posten.</div>}
				{doc && !doc.error && (
					<>
						<div className="drawer__sub">
							{doc.description ?? doc.CustomerName ?? doc.SupplierName ?? doc.name ?? ''}
						</div>
						<dl className="kv">
							{Object.entries(doc)
								.filter(([k, v]) => !['rows', 'InvoiceRows', '_id', 'updated', '__v'].includes(k)
									&& v !== null && v !== '' && typeof v !== 'object')
								.map(([k, v]) => (
									<div key={k} style={{display: 'contents'}}>
										<dt>{k}</dt><dd>{String(v)}</dd>
									</div>
								))}
						</dl>
						{lines && (
							<div className="table-wrap">
								<table style={{minWidth: 0}}>
									<thead><tr>
										{Object.keys(lines[0] ?? {}).filter(k => k !== '_id')
											.map(k => <th key={k}>{k}</th>)}
									</tr></thead>
									<tbody>
										{lines.map((row, i) => (
											<tr key={i}>
												{Object.entries(row).filter(([k]) => k !== '_id').map(([k, v]) => (
													<td key={k}>{v == null ? '—' : String(v)}</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

/** Which collections can be written back to Fortnox. Vouchers and invoices cannot:
 *  this app reports on the books, it does not keep them. */
const EDITABLE = {customers: 'customers', suppliers: 'suppliers', articles: 'articles'};

export default function Browse({collection}) {
	const [open, setOpen] = useState(null);
	const [editing, setEditing] = useState(null);
	const [nonce, setNonce] = useState(0);
	const view = VIEWS[collection];
	const editable = EDITABLE[collection];
	return (
		<>
			{editable && (
				<p className="sub">
					Klicka en rad för att se posten. Redigera skriver direkt till Fortnox-registret —
					endast ändrade fält skickas.
				</p>
			)}
			<DataTable key={nonce} collection={collection} columns={view.columns}
				filters={view.filters}
				fieldColumn={view.fieldColumns ? fieldColumn : undefined} wide={view.wide}
				onRow={r => (editable ? setEditing(r._id) : setOpen(r._id))} />
			{open != null && (
				<Detail key={open} collection={collection} id={open} onClose={() => setOpen(null)} />
			)}
			{editing != null && (
				<EditDrawer key={editing} resource={editable} id={editing}
					onClose={() => setEditing(null)} onSaved={() => setNonce(n => n + 1)} />
			)}
		</>
	);
}
